import { Button, DatePicker, Input, useDebounceCallback } from "@devhop/ui";
import { CalendarIcon, SearchIcon } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { formatDateForInput } from "@/shared/lib/date-time";
import { useCallback, useEffect, useMemo, useState } from "react";

const VALUE_ALL = "__all__";
const STATUS_OPTIONS = [
  { value: VALUE_ALL, label: "ທຸກສະຖານະ" },
  { value: "draft", label: "ຮ່າງ" },
  { value: "confirmed", label: "ຢືນຢັນ" },
  { value: "paid", label: "ຊຳລະແລ້ວ" },
];

export type PayrollHistorySearchParams = {
  offset?: number;
  limit?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  periodYear?: number;
  periodMonth?: number;
  status?: string;
  paidAtFrom?: string;
  paidAtTo?: string;
  search?: string;
};

type PayrollHistoryFilterProps = {
  searchParams: PayrollHistorySearchParams;
  /** When true, status is fixed to "paid" and the status dropdown is hidden (e.g. history page). */
  paidOnly?: boolean;
};

const MONTH_OPTIONS = [
  { value: VALUE_ALL, label: "ທຸກເດືອນ" },
  { value: "1", label: "ມ.ກ." },
  { value: "2", label: "ກ.ພ." },
  { value: "3", label: "ມ.ນ." },
  { value: "4", label: "ມ.ສ." },
  { value: "5", label: "ພ.ສ." },
  { value: "6", label: "ມ.ຖ." },
  { value: "7", label: "ກ.ກ." },
  { value: "8", label: "ສ.ຫ." },
  { value: "9", label: "ກ.ຍ." },
  { value: "10", label: "ຕ.ລ." },
  { value: "11", label: "ພ.ຈ." },
  { value: "12", label: "ທ.ວ." },
];

export function PayrollHistoryFilter({ searchParams, paidOnly = false }: PayrollHistoryFilterProps) {
  const nav = useNavigate({ from: "/app/hr/payroll/history" });
  const search = searchParams;
  const [searchInput, setSearchInput] = useState(search.search ?? "");
  const [statusInput, setStatusInput] = useState(paidOnly ? "paid" : (search.status ?? VALUE_ALL));
  const [monthInput, setMonthInput] = useState(
    search.periodMonth != null ? String(search.periodMonth) : VALUE_ALL,
  );
  const [paidFromInput, setPaidFromInput] = useState(search.paidAtFrom ?? "");
  const [paidToInput, setPaidToInput] = useState(search.paidAtTo ?? "");

  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(
    () =>
      [currentYear, currentYear - 1, currentYear - 2].map((y) => ({
        value: String(y),
        label: String(y),
      })),
    [currentYear],
  );
  const [yearInput, setYearInput] = useState(
    search.periodYear != null ? String(search.periodYear) : String(currentYear),
  );

  useEffect(() => {
    setSearchInput(search.search ?? "");
    setStatusInput(paidOnly ? "paid" : (search.status ?? VALUE_ALL));
    setMonthInput(search.periodMonth != null ? String(search.periodMonth) : VALUE_ALL);
    setYearInput(search.periodYear != null ? String(search.periodYear) : String(currentYear));
    setPaidFromInput(search.paidAtFrom ?? "");
    setPaidToInput(search.paidAtTo ?? "");
  }, [
    search.search,
    search.status,
    search.periodMonth,
    search.periodYear,
    search.paidAtFrom,
    search.paidAtTo,
    currentYear,
    paidOnly,
  ]);

  const updateSearch = useCallback(
    (opts: {
      year?: string;
      month?: string;
      status?: string;
      paidFrom?: string;
      paidTo?: string;
      searchVal?: string;
    }) => {
      const yr = opts.year ?? yearInput;
      const mon = opts.month ?? monthInput;
      const st = opts.status ?? statusInput;
      const pf = opts.paidFrom ?? paidFromInput;
      const pt = opts.paidTo ?? paidToInput;
      const sv = opts.searchVal ?? searchInput;
      nav({
        search: {
          offset: 0,
          limit: search.limit ?? 20,
          sort: search.sort,
          periodYear: yr ? Number(yr) : undefined,
          periodMonth: mon && mon !== VALUE_ALL ? Number(mon) : undefined,
          status: paidOnly ? "paid" : (st === VALUE_ALL ? VALUE_ALL : st),
          paidAtFrom: pf.trim() || undefined,
          paidAtTo: pt.trim() || undefined,
          search: sv.trim() || undefined,
        },
      });
    },
    [nav, search.limit, search.sort, paidOnly, yearInput, monthInput, statusInput, paidFromInput, paidToInput, searchInput],
  );

  const debouncedSearch = useDebounceCallback((val: string) => {
    updateSearch({ searchVal: val });
  }, 400);

  const resetFilters = () => {
    setSearchInput("");
    setStatusInput(paidOnly ? "paid" : VALUE_ALL);
    setMonthInput(VALUE_ALL);
    setYearInput(String(currentYear));
    setPaidFromInput("");
    setPaidToInput("");
    nav({
      search: {
        offset: 0,
        limit: 20,
        sort: search.sort,
        periodYear: undefined,
        periodMonth: undefined,
        status: VALUE_ALL,
        paidAtFrom: undefined,
        paidAtTo: undefined,
        search: undefined,
      },
    });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-2xs">
          <SearchIcon
            className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id="history-search-employee"
            aria-label="ຊື່ ຫຼື ລະຫັດພະນັກງານ"
            placeholder="ຄົ້ນຫາພະນັກງານ..."
            className="h-8 pl-8"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              debouncedSearch(e.target.value);
            }}
          />
        </div>
        <SimpleSelect
          options={yearOptions}
          value={yearInput}
          onValueChange={(v) => {
            setYearInput(v);
            updateSearch({ year: v });
          }}
          placeholder="ປີ"
          className="h-8 w-20"
          aria-label="ປີ"
        />
        <SimpleSelect
          options={MONTH_OPTIONS}
          value={monthInput}
          onValueChange={(v) => {
            setMonthInput(v);
            updateSearch({ month: v });
          }}
          placeholder="ເດືອນ"
          className="h-8 w-24"
          aria-label="ເດືອນ"
        />
        {!paidOnly && (
          <SimpleSelect
            options={STATUS_OPTIONS}
            value={statusInput}
            onValueChange={(v) => {
              setStatusInput(v);
              updateSearch({ status: v });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-28"
            aria-label="ສະຖານະ"
          />
        )}
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden />
          <DatePicker
            mode="range"
            value={{
              from: paidFromInput ? new Date(paidFromInput) : undefined,
              to: paidToInput ? new Date(paidToInput) : undefined,
            }}
            onChange={(range) => {
              const from: Date | undefined = Array.isArray(range)
                ? range[0]
                : (range as { from?: Date; to?: Date } | undefined)?.from;
              const to: Date | undefined = Array.isArray(range)
                ? range[1]
                : (range as { from?: Date; to?: Date } | undefined)?.to;
              const pf = from ? formatDateForInput(from) : "";
              const pt = to ? formatDateForInput(to) : "";
              setPaidFromInput(pf);
              setPaidToInput(pt);
              updateSearch({ paidFrom: pf, paidTo: pt });
            }}
            placeholder="ວັນທີຈ່າຍ"
            className="h-8 w-full min-w-0 sm:w-48"
          />
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={resetFilters} className="h-8">
        ລ້າງກັ່ນຕອງ
      </Button>
    </div>
    </div>
  );
}
